import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { apiClient, authHeaders } from "../lib/api";

const StoreContext = createContext(null);

const readStorage = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const parseSizeStock = (product, size) => {
  if (!product || !size) return null;
  const inventory = product.size_inventory;
  if (!inventory || typeof inventory !== "object" || Array.isArray(inventory)) return null;
  const value = inventory[size];
  return Number.isFinite(Number(value)) ? Number(value) : 0;
};

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState(() => readStorage("eo_cart", []));
  const [wishlist, setWishlist] = useState(() => readStorage("eo_wishlist", []));
  const [token, setToken] = useState(() => localStorage.getItem("eo_token") || "");
  const [user, setUser] = useState(() => readStorage("eo_user", null));
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    localStorage.setItem("eo_cart", JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem("eo_wishlist", JSON.stringify(wishlist));
  }, [wishlist]);

  useEffect(() => {
    if (token) {
      localStorage.setItem("eo_token", token);
    } else {
      localStorage.removeItem("eo_token");
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      localStorage.setItem("eo_user", JSON.stringify(user));
    } else {
      localStorage.removeItem("eo_user");
    }
  }, [user]);

  const loadProfile = async () => {
    if (!token) return;
    setLoadingProfile(true);
    try {
      const [meRes, wishlistRes] = await Promise.all([
        apiClient.get("/auth/me", authHeaders(token)),
        apiClient.get("/wishlist", authHeaders(token)),
      ]);
      setUser(meRes.data);
      setWishlist(wishlistRes.data.wishlist || []);
    } catch {
      setToken("");
      setUser(null);
      toast.error("Session expired. Please sign in again.");
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, [token]);

  const signup = async (payload) => {
    const response = await apiClient.post("/auth/signup", payload);
    setToken(response.data.token);
    setUser(response.data.user);
    toast.success("Welcome to Eternal Order.");
  };

  const login = async (payload) => {
    const response = await apiClient.post("/auth/login", payload);
    setToken(response.data.token);
    setUser(response.data.user);
    toast.success("Signed in successfully.");
  };

  const logout = () => {
    setToken("");
    setUser(null);
    toast.success("Logged out.");
  };

  const resolveItemKey = (item) => item.itemKey || `${item.product.id}-${item.size}`;

  const addToCart = (product, size, quantity = 1, options = {}) => {
    const incomingItemKey = options.itemKey || options.customCartKey || `${product.id}-${size}`;

    if (!product.preorder) {
      const sizeStock = parseSizeStock(product, size);
      const cartQty = cart
        .filter((item) => item.product.id === product.id && item.size === size)
        .reduce((acc, item) => acc + item.quantity, 0);
      if (sizeStock !== null && cartQty + quantity > sizeStock) {
        toast.error(`${size} size is out of stock`);
        return;
      }
    }

    setCart((prev) => {
      const existing = prev.find((item) => resolveItemKey(item) === incomingItemKey);
      if (existing) {
        const sizeStock = parseSizeStock(product, size);
        const nextQuantity = Math.min(10, existing.quantity + quantity);
        if (!product.preorder && sizeStock !== null && nextQuantity > sizeStock) {
          toast.error(`${size} size is out of stock`);
          return prev;
        }
        return prev.map((item) =>
          resolveItemKey(item) === incomingItemKey
            ? { ...item, quantity: nextQuantity }
            : item,
        );
      }
      return [
        ...prev,
        {
          product,
          size,
          quantity,
          itemKey: incomingItemKey,
          customDesign: options.customDesign || null,
        },
      ];
    });
    toast.success("Added to cart");
  };

  const updateCartQuantity = (productId, size, quantity, itemKey = "") => {
    if (quantity <= 0) {
      removeFromCart(productId, size, itemKey);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        (itemKey ? resolveItemKey(item) === itemKey : item.product.id === productId && item.size === size)
          ? (() => {
              const sizeStock = parseSizeStock(item.product, item.size);
              const safeQuantity = Math.min(10, quantity);
              if (!item.product.preorder && sizeStock !== null && safeQuantity > sizeStock) {
                toast.error(`${item.size} size is out of stock`);
                return { ...item, quantity: sizeStock };
              }
              return { ...item, quantity: safeQuantity };
            })()
          : item,
      ),
    );
  };

  const listStoreReviews = async (limit = 20) => {
    const response = await apiClient.get(`/reviews?limit=${limit}`);
    return response.data;
  };

  const submitStoreReview = async (payload) => {
    if (!token) throw new Error("Sign in required");
    const response = await apiClient.post("/reviews", payload, authHeaders(token));
    return response.data;
  };

  const removeFromCart = (productId, size, itemKey = "") => {
    setCart((prev) =>
      prev.filter((item) =>
        itemKey ? resolveItemKey(item) !== itemKey : !(item.product.id === productId && item.size === size),
      ),
    );
    toast.success("Removed from cart");
  };

  const clearCart = () => setCart([]);

  const toggleWishlist = async (productId) => {
    const exists = wishlist.includes(productId);
    if (!token) {
      setWishlist((prev) => (exists ? prev.filter((id) => id !== productId) : [...prev, productId]));
      toast.success(exists ? "Removed from wishlist" : "Saved to wishlist");
      return;
    }

    if (exists) {
      const response = await apiClient.delete(`/wishlist/${productId}`, authHeaders(token));
      setWishlist(response.data.wishlist || []);
      toast.success("Removed from wishlist");
      return;
    }

    const response = await apiClient.post(`/wishlist/${productId}`, {}, authHeaders(token));
    setWishlist(response.data.wishlist || []);
    toast.success("Saved to wishlist");
  };

  const placeOrder = async (payload) => {
    const response = await apiClient.post("/orders", payload, authHeaders(token));
    return response.data;
  };

  const createRazorpayOrder = async (payload) => {
    const response = await apiClient.post("/payments/razorpay/create-order", payload, authHeaders(token));
    return response.data;
  };

  const verifyRazorpayPayment = async (payload) => {
    const response = await apiClient.post("/payments/razorpay/verify", payload, authHeaders(token));
    return response.data;
  };

  const getPricingQuote = async (payload) => {
    const response = await apiClient.post("/pricing/quote", payload, authHeaders(token));
    return response.data;
  };

  const validateOffer = async (payload) => {
    const response = await apiClient.post("/offers/validate", payload, authHeaders(token));
    return response.data;
  };

  const getPublicOffers = async (kind = "all") => {
    const response = await apiClient.get(`/offers/public?kind=${kind}`);
    return response.data;
  };

  const listCouponsAdmin = async (adminKey) => {
    const response = await apiClient.get("/admin/coupons", {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const createCouponAdmin = async (payload, adminKey) => {
    const response = await apiClient.post("/admin/coupons", payload, {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const updateCouponAdmin = async (couponId, payload, adminKey) => {
    const response = await apiClient.put(`/admin/coupons/${couponId}`, payload, {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const deleteCouponAdmin = async (couponId, adminKey) => {
    const response = await apiClient.delete(`/admin/coupons/${couponId}`, {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const listAdminOrders = async (adminKey, filters = {}) => {
    const query = new URLSearchParams(
      Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {}),
    ).toString();

    const response = await apiClient.get(`/admin/orders${query ? `?${query}` : ""}`, {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const downloadAdminOrdersExportCsv = async (adminKey, filters = {}) => {
    const query = new URLSearchParams(
      Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {}),
    ).toString();

    const response = await apiClient.get(`/admin/orders/export/csv${query ? `?${query}` : ""}`, {
      headers: {
        "x-admin-key": adminKey,
      },
      responseType: "blob",
    });
    return response;
  };

  const downloadAdminOrdersExportXlsx = async (adminKey, filters = {}) => {
    const query = new URLSearchParams(
      Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {}),
    ).toString();

    const response = await apiClient.get(`/admin/orders/export/xlsx${query ? `?${query}` : ""}`, {
      headers: {
        "x-admin-key": adminKey,
      },
      responseType: "blob",
    });
    return response;
  };

  const updateAdminOrderStatus = async (orderId, status, adminKey) => {
    const response = await apiClient.put(
      `/admin/orders/${orderId}/status`,
      { status },
      {
        headers: {
          "x-admin-key": adminKey,
        },
      },
    );
    return response.data;
  };

  const syncAdminOrderToNimbuspost = async (orderId, adminKey) => {
    const response = await apiClient.post(
      `/admin/orders/${orderId}/nimbuspost-sync`,
      {},
      {
        headers: {
          "x-admin-key": adminKey,
        },
      },
    );
    return response.data;
  };

  const getAdminNimbuspostHealth = async (adminKey) => {
    const response = await apiClient.get("/admin/nimbuspost/health", {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const reconnectAdminNimbuspost = async (adminKey) => {
    const response = await apiClient.post(
      "/admin/nimbuspost/reconnect",
      {},
      {
        headers: {
          "x-admin-key": adminKey,
        },
      },
    );
    return response.data;
  };

  const listAdminLeads = async (adminKey, filters = {}) => {
    const query = new URLSearchParams(
      Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {}),
    ).toString();

    const response = await apiClient.get(`/admin/leads${query ? `?${query}` : ""}`, {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const listAdminUsers = async (adminKey, filters = {}) => {
    const query = new URLSearchParams(
      Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      }, {}),
    ).toString();

    const response = await apiClient.get(`/admin/users${query ? `?${query}` : ""}`, {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const listGlobalStockAdmin = async (adminKey) => {
    const response = await apiClient.get("/admin/stock/colors", {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const updateGlobalStockAdmin = async (payload, adminKey) => {
    const response = await apiClient.put("/admin/stock/colors", payload, {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const listGlobalStockSubscriptionsAdmin = async (adminKey, status = "all") => {
    const response = await apiClient.get(`/admin/stock/subscriptions?status=${status}`, {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const createBackInStockSubscription = async (payload) => {
    const response = await apiClient.post("/stock-alert-subscriptions", payload, authHeaders(token));
    return response.data;
  };

  const updateAdminUserStatus = async (userId, isActive, adminKey) => {
    const response = await apiClient.put(
      `/admin/users/${userId}/status`,
      { is_active: isActive },
      {
        headers: {
          "x-admin-key": adminKey,
        },
      },
    );
    return response.data;
  };

  const deleteAdminUser = async (userId, adminKey) => {
    const response = await apiClient.delete(`/admin/users/${userId}`, {
      headers: {
        "x-admin-key": adminKey,
      },
    });
    return response.data;
  };

  const fetchOrderHistory = async () => {
    if (!token) return [];
    const response = await apiClient.get("/orders/history", authHeaders(token));
    return response.data.orders || [];
  };

  const addAddress = async (payload) => {
    if (!token) throw new Error("Sign in required");
    const response = await apiClient.post("/addresses", payload, authHeaders(token));
    const updated = { ...user, addresses: response.data.addresses || [] };
    setUser(updated);
  };

  const cartCount = useMemo(() => cart.reduce((acc, item) => acc + item.quantity, 0), [cart]);
  const cartSubtotal = useMemo(
    () => cart.reduce((acc, item) => acc + item.product.selling_price * item.quantity, 0),
    [cart],
  );

  const value = {
    cart,
    wishlist,
    token,
    user,
    loadingProfile,
    cartCount,
    cartSubtotal,
    signup,
    login,
    logout,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    clearCart,
    toggleWishlist,
    placeOrder,
    createRazorpayOrder,
    verifyRazorpayPayment,
    getPricingQuote,
    validateOffer,
    getPublicOffers,
    listCouponsAdmin,
    createCouponAdmin,
    updateCouponAdmin,
    deleteCouponAdmin,
    listAdminOrders,
    downloadAdminOrdersExportCsv,
    downloadAdminOrdersExportXlsx,
    updateAdminOrderStatus,
    syncAdminOrderToNimbuspost,
    getAdminNimbuspostHealth,
    reconnectAdminNimbuspost,
    listAdminLeads,
    listAdminUsers,
    updateAdminUserStatus,
    deleteAdminUser,
    listGlobalStockAdmin,
    updateGlobalStockAdmin,
    listGlobalStockSubscriptionsAdmin,
    createBackInStockSubscription,
    fetchOrderHistory,
    addAddress,
    listStoreReviews,
    submitStoreReview,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error("useStore must be used within StoreProvider");
  }
  return context;
};
